import { useEffect, useState } from 'react';
import { intelAsk, intelThread, type IntelThread } from '@aithora/shared';
import { useQuery } from '../../useQuery';
import { sx } from '../../ui/designStyles';
import { ErrorState, Skeleton } from '../../ui/states';
import { A, DASH, plusK, useBreakdown, type TabProps } from './common';

type Turn = IntelThread['turns'][number] & { pending?: boolean; failed?: boolean };

/** Provider ids as people know them. */
const WHO: Record<string, string> = { anthropic: 'Claude', perplexity: 'Perplexity', mistral: 'Mistral', google: 'Gemini', gemini: 'Gemini', local: 'the Aithora model', openai: 'OpenAI' };
const who = (p: string | null | undefined) => (p ? WHO[p.toLowerCase()] ?? p : null);

/** "Answered by Claude · Web research by Perplexity": what backed an answer, from what the server recorded. */
export function backedBy(t: { provider?: string | null; live?: boolean; research?: string | null }): string | null {
  const parts: string[] = [];
  const a = who(t.provider);
  if (a) parts.push(t.live === false ? 'Not a live model answer' : 'Answered by ' + a);
  const r = who(t.research);
  if (r) parts.push('Web research by ' + r);
  return parts.length ? parts.join(' · ') : null;
}

const host = (u: string) => {
  try { return new URL(u).hostname.replace(/^www\./, ''); } catch { return u; }
};

/** The questions offered (design atqPrompts). */
const PROMPTS = [
  'Find undervalued memory-care in Texas with occupancy above 85%',
  'Which states have the strongest 65+ demand vs facility supply?',
  'Compare our acquired facilities to their state peer benchmarks',
  'Screen for distressed SNFs: low ratings, high deficiencies, weak margin',
  'What does the MODEL badge on Performance actually mean?',
];

/** The stages of an answer, each its own server-sent event (design atqStages). */
const STAGES: [string, string][] = [
  ['Routed', 'intent, tier and model chosen from your role, not from the question text'],
  ['Retrieved', 'real tool calls against the catalog, not a canned list'],
  ['Answered', 'streamed sentence by sentence as answer-chunk events'],
  ['Gated', 'PASS, HOLD or BLOCK with the checks that ran'],
  ['Cited', 'the reviewable basis, so the answer can be checked rather than trusted'],
];

const BADGE = 'display:inline-flex;align-items:center;height:22px;padding:0 8px;border-radius:5px;font-variant-numeric:tabular-nums;font-size:14px;font-weight:700;letter-spacing:.06em;white-space:nowrap;';

/**
 * Atlas (design aiIsAtq, T14958; aiNsVals L29210): the catalog-wide analyst. The thread is
 * yours and kept (GET /intelligence/atlas/thread); each question goes to POST
 * /api/atlas/ask, streamed, gated and cited.
 */
export function AtlasTab(p: TabProps) {
  const { client, readOnly } = p;
  const bd = useBreakdown(client);
  const thread = useQuery<IntelThread>((signal) => intelThread(client, { signal }), [client]);
  const [turns, setTurns] = useState<Turn[]>([]);
  const [gate, setGate] = useState<IntelThread['gate']>(null);
  const [input, setInput] = useState('');
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    if (thread.data) {
      setTurns(thread.data.turns);
      setGate(thread.data.gate);
    }
  }, [thread.data]);
  const total = bd.data ? plusK(bd.data.total) : DASH;

  const ask = async (question: string) => {
    const q = question.trim();
    if (!q || busy || readOnly) return;
    setBusy(true);
    setInput('');
    const history = turns.filter((t) => !t.pending && !t.failed).map((t) => ({ role: t.role, content: t.text }));
    setTurns((ts) => [...ts, { role: 'user', text: q }, { role: 'assistant', text: '', pending: true }]);
    try {
      const r = await intelAsk(client, { question: q, history }, {
        onChunk: (c) => setTurns((ts) => ts.map((t, i) => (i === ts.length - 1 ? { ...t, text: t.text + c } : t))),
      });
      setTurns((ts) => ts.map((t, i) => (i === ts.length - 1 ? { role: 'assistant', text: r.answer, tools: r.tools, cites: r.cites, provider: r.provider, model: r.model, live: r.live, research: r.research, webSources: r.webSources } : t)));
      setGate(r.gate);
    } catch (e) {
      setTurns((ts) => ts.map((t, i) => (i === ts.length - 1 ? { role: 'assistant', text: (e as Error).message, failed: true } : t)));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div style={sx('display:flex;flex-direction:column;gap:13px;min-width:0;')}>
      <div>
        <div style={sx('font-size:21px;font-weight:700;letter-spacing:-.018em;')}>Ask Atlas anything about the catalog</div>
        <div style={sx('display:flex;align-items:center;gap:7px;flex-wrap:wrap;row-gap:5px;margin:6px 0 5px;')}>
          <span style={sx(BADGE + 'color:#1F6B52;background:rgba(47,138,111,.14);')}>LIVE · each answer names who wrote and researched it</span>
          {gate && <span style={sx(BADGE + (gate === 'PASS' ? 'color:#2F5C97;background:rgba(79,138,214,.14);' : 'color:#B3261E;background:rgba(179,38,30,.13);'))}>{'GATE · ' + gate}</span>}
          <span style={sx('font-variant-numeric:tabular-nums;font-size:14px;color:#54606C;white-space:nowrap;')}>{total + ' facilities queryable · live count'}</span>
        </div>
        <div style={sx('font-size:14px;color:#54606C;margin-bottom:7px;line-height:1.5;max-width:84ch;text-wrap:pretty;')}>The acquisition catalog is operator-only and fails closed. Facility staff reach their own aggregate benchmark, never the catalog.</div>

        <div style={sx('border-top:1px solid rgba(34,39,46,.14);padding:9px 2px;margin-bottom:11px;')}>
          <div style={sx('font-size:14px;letter-spacing:.11em;text-transform:uppercase;color:#54606C;font-weight:700;margin-bottom:10px;')}>How an answer arrives</div>
          <div style={sx('display:grid;grid-template-columns:repeat(auto-fit,minmax(178px,1fr));gap:11px;')}>
            {STAGES.map(([label, note], i) => (
              <div key={label} style={sx('display:flex;align-items:flex-start;gap:9px;')}>
                <span style={sx('display:flex;align-items:center;justify-content:center;width:19px;height:22px;flex:0 0 19px;border-radius:50%;font-size:14px;font-weight:700;background:rgba(47,138,111,.16);color:#1F6B52;')}>{String(i + 1)}</span>
                <span style={sx('min-width:0;flex:1 1 auto;')}>
                  <span style={sx('display:block;font-size:16px;font-weight:700;')}>{label}</span>
                  <span style={sx('display:block;font-size:14px;color:#54606C;line-height:1.45;margin-top:2px;text-wrap:pretty;')}>{note}</span>
                </span>
              </div>
            ))}
          </div>
          <div style={sx('font-size:14px;color:#54606C;line-height:1.5;margin-top:11px;max-width:88ch;text-wrap:pretty;')}>Each stage arrives as its own event, so you watch the answer being built rather than waiting on a spinner. The output gate screens every sentence before it reaches you.</div>
        </div>

        <div style={sx('background:rgba(179,38,30,.06);border:1px solid rgba(179,38,30,.28);border-radius:11px;padding:9px 2px;margin-bottom:11px;')}>
          <div style={sx('display:flex;align-items:center;gap:9px;flex-wrap:wrap;row-gap:5px;')}>
            <span style={sx('font-size:16px;font-weight:700;')}>When a sentence is withheld</span>
            <span style={sx('display:inline-flex;align-items:center;height:22px;padding:0 8px;border-radius:5px;font-size:14px;font-weight:700;letter-spacing:.06em;white-space:nowrap;color:#B3261E;background:rgba(179,38,30,.13);')}>SAFE_FALLBACK</span>
          </div>
          <div style={sx('font-size:16px;color:#3C454F;line-height:1.6;margin-top:7px;max-width:88ch;text-wrap:pretty;')}>The output gate reads each streamed sentence before you see it. If one fails, that sentence is replaced rather than the whole answer being thrown away, and you are told which check stopped it.</div>
          <div style={sx('border-top:1px solid rgba(34,39,46,.14);padding:11px 13px;margin-top:10px;')}>
            <div style={sx('font-size:16px;color:#B3261E;line-height:1.55;text-wrap:pretty;')}>One sentence was withheld: it named a resident in a thread scoped to a facility aggregate.</div>
            <div style={sx('font-size:16px;font-weight:700;color:#22272E;line-height:1.55;margin-top:5px;text-wrap:pretty;')}>Ask again with the resident selected, or open their chart directly.</div>
          </div>
          <div style={sx('font-size:14px;color:#54606C;line-height:1.5;margin-top:9px;max-width:88ch;text-wrap:pretty;')}>A withheld sentence is visible on purpose. Silently shortening an answer would leave you trusting a result you cannot check.</div>
        </div>
        <div style={sx('font-size:14px;color:#54606C;margin-bottom:7px;line-height:1.5;max-width:84ch;text-wrap:pretty;')}>
          Atlas also reads the console guide, so {'“'}what does this page do{'”'} and {'“'}what does this badge mean{'”'} are answerable in the same thread as a catalog query. Answers cite the guide entry rather than paraphrasing it.
        </div>
        <div style={sx('font-size:16px;color:#54606C;')}>{'Agentic analyst over all ' + total + ' facilities - it pulls the data and runs the models for you.'}</div>
      </div>
      <div style={sx('display:flex;flex-direction:column;gap:7px;')}>
        {PROMPTS.map((q) => (
          <A key={q} hover="intel-h-prompt" onClick={() => void ask(q)} disabled={readOnly || busy} style="box-sizing:border-box;border-top:1px solid rgba(34,39,46,.14);padding:11px 14px;font-size:16px;color:#3C454F;text-decoration:none;">
            {q}
          </A>
        ))}
      </div>
      <div style={sx('display:flex;flex-direction:column;gap:11px;')} aria-live="polite">
        {thread.loading && !thread.data && <Skeleton rows={3} label="Loading your thread" />}
        {thread.error && !thread.data && <ErrorState error={thread.error} onRetry={thread.reload} />}
        {turns.map((m, i) => {
          const me = m.role === 'user';
          const hasTools = !me && !!m.tools?.length;
          return (
            <div key={i} style={sx('display:flex;gap:10px;' + (me ? 'justify-content:flex-end;' : ''))}>
              <div
                style={sx(
                  'box-sizing:border-box;max-width:76ch;border-radius:11px;padding:11px 14px;font-size:16px;line-height:1.55;text-wrap:pretty;' +
                    (me ? 'background:rgba(79,138,214,.14);color:#22272E;' : 'background:#FCFAF4;border:1px solid rgba(34,39,46,.11);color:' + (m.failed ? '#B3261E' : '#22272E') + ';'),
                )}
              >
                {m.pending && !m.text ? 'Atlas is working on it…' : m.text}
                {hasTools && (
                  <span style={sx('display:flex;align-items:center;gap:6px;flex-wrap:wrap;row-gap:6px;margin-top:10px;padding-top:9px;border-top:1px solid rgba(34,39,46,.1);')}>
                    <span style={sx('font-size:14px;letter-spacing:.09em;text-transform:uppercase;white-space:nowrap;color:#54606C;font-weight:600;')}>ran</span>
                    {m.tools!.map((t) => (
                      <span key={t} style={sx('font-variant-numeric:tabular-nums;font-size:14px;color:#2F5C97;background:rgba(79,138,214,.12);border-radius:4px;padding:2px 7px;')}>
                        {t}
                      </span>
                    ))}
                    <span style={sx('flex:1;')} />
                    {(m.cites ?? []).map((c) => (
                      <span key={c} style={sx('font-size:14px;color:#54606C;background:rgba(34,39,46,.07);border-radius:4px;padding:2px 7px;')}>
                        {c}
                      </span>
                    ))}
                  </span>
                )}
                {!me && !m.pending && !m.failed && backedBy(m) && (
                  <span data-testid="atlas-backed-by" style={sx('display:flex;align-items:center;gap:6px;flex-wrap:wrap;row-gap:4px;margin-top:8px;font-size:14px;color:#54606C;')}>
                    <span style={sx('font-weight:600;')}>{backedBy(m)}</span>
                    {(m.webSources ?? []).slice(0, 6).map((u) => (
                      <a key={u} href={u} target="_blank" rel="noopener noreferrer" style={sx('color:#2F5C97;text-decoration:none;background:rgba(79,138,214,.1);border-radius:4px;padding:1px 6px;')}>
                        {host(u)}
                      </a>
                    ))}
                  </span>
                )}
              </div>
            </div>
          );
        })}
      </div>
      <div style={sx('display:flex;align-items:center;gap:9px;flex-wrap:wrap;row-gap:6px;')}>
        <input
          type="text"
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') void ask(input);
          }}
          disabled={readOnly}
          placeholder={'Ask Atlas about the catalog…'}
          aria-label="Ask Atlas about the catalog"
          style={sx('flex:1 1 130px;min-width:min(100%,130px);box-sizing:border-box;height:42px;padding:0 14px;border-top:1px solid rgba(34,39,46,.14);font-size:16px;color:#22272E;')}
        />
        <A onClick={() => void ask(input)} disabled={readOnly || busy || !input.trim()} title={!readOnly && !busy && !input.trim() ? 'Type a question first' : undefined} style="display:inline-flex;align-items:center;height:42px;padding:0 19px;border-radius:9px;font-size:16px;font-weight:600;text-decoration:none;background:#2F5C97;color:#fff;white-space:nowrap;">
          Ask
        </A>
      </div>
    </div>
  );
}
